/*
  Collection and search pages: filters, sort and "Load more" without a full page load.
  Re-renders the section through the Section Rendering API (?section_id=) and keeps the URL in step, so the back
  button and shared links work. Without JavaScript the filter form and links work as normal pages.
*/
const rootSelector = '[data-collection-root]';
let busy = false;
let pending = null; // a change made while a render was running: rendered straight after, never dropped

const sectionId = () => document.querySelector(rootSelector)?.dataset.sectionId;

async function render(url, { push = true, append = false } = {}) {
  if (busy) {
    pending = { url, push, append };
    return;
  }
  busy = true;
  const root = document.querySelector(rootSelector);
  root?.setAttribute('aria-busy', 'true');
  root?.classList.add('opacity-60', 'transition-opacity');
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId());
    const html = await fetch(u).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(rootSelector);
    if (!fresh) {
      window.location.href = url;
      return;
    }
    if (append) {
      const grid = root.querySelector('[data-product-grid]');
      fresh.querySelectorAll('[data-product-grid] > li').forEach((li) => grid.append(li));
      root.querySelector('nav[aria-label="Pages"]')?.replaceWith(fresh.querySelector('nav[aria-label="Pages"]') || '');
    } else {
      // Keep open filter groups open across renders.
      const open = [...root.querySelectorAll('[data-facets-form] details')].map((d) => d.open);
      const drawerOpen = document.getElementById('FacetsDrawer')?.hasAttribute('open');
      // Remember what had focus (a filter checkbox, the sort menu) so keyboard users stay where they were.
      const active = document.activeElement;
      const focusKey = active && root.contains(active) ? { id: active.id, name: active.name, value: active.value } : null;
      root.replaceWith(fresh);
      fresh.querySelectorAll('[data-facets-form] details').forEach((d, i) => {
        if (open[i] !== undefined) d.open = open[i];
      });
      const trigger = fresh.querySelector('[data-drawer-open="FacetsDrawer"]');
      if (drawerOpen) {
        const drawer = document.getElementById('FacetsDrawer');
        drawer?.setAttribute('open', '');
        if (drawer) drawer.opener = trigger;
        trigger?.setAttribute('aria-expanded', 'true');
      }
      if (focusKey) {
        const target =
          (focusKey.id && fresh.querySelector(`#${CSS.escape(focusKey.id)}`)) ||
          (focusKey.name && [...fresh.querySelectorAll(`[name="${CSS.escape(focusKey.name)}"]`)].find((el) => el.value === focusKey.value));
        target?.focus({ preventScroll: true });
      }
    }
    // Brand and product type pages have their own title (snippets/collection-landing).
    const docTitle = document.querySelector(rootSelector)?.dataset.docTitle;
    if (docTitle && !append) document.title = docTitle;
    const clean = new URL(url, window.location.origin);
    // Only touch the query when needed: rewriting it turns %20 into "+", which robots.txt blocks on collections.
    if (clean.searchParams.has('section_id')) clean.searchParams.delete('section_id');
    if (push) window.history.pushState({ collection: true }, '', clean);
  } catch {
    window.location.href = url;
  } finally {
    busy = false;
    const current = document.querySelector(rootSelector);
    current?.removeAttribute('aria-busy');
    current?.classList.remove('opacity-60');
    if (pending) {
      const next = pending;
      pending = null;
      render(next.url, next);
    }
  }
}

function urlFromForm(form) {
  const data = new FormData(form);
  const sort = document.querySelector('[data-sort]');
  if (sort) data.set('sort_by', sort.value);
  const params = new URLSearchParams();
  for (const [k, v] of data) if (v !== '') params.append(k, v);
  return `${form.getAttribute('action')}?${params}`;
}

let timer;
document.addEventListener('change', (e) => {
  const form = e.target.closest('[data-facets-form]');
  const sort = e.target.closest('[data-sort]');
  if (!form && !sort) return;
  const target = form || document.querySelector('[data-facets-form]');
  if (!target) {
    const u = new URL(window.location.href);
    u.searchParams.set('sort_by', sort.value);
    u.searchParams.delete('page');
    return render(u.toString());
  }
  clearTimeout(timer);
  timer = setTimeout(() => render(urlFromForm(target)), e.target.type === 'number' ? 600 : 0);
});

document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-facets-form]');
  if (!form) return;
  e.preventDefault();
  render(urlFromForm(form));
});

document.addEventListener('click', (e) => {
  const chip = e.target.closest('[data-facet-link]');
  if (chip) {
    e.preventDefault();
    render(chip.href);
    return;
  }
  const more = e.target.closest('[data-load-more]');
  if (more) {
    e.preventDefault();
    more.textContent = 'Loading';
    render(more.href, { push: false, append: true });
  }
});

window.addEventListener('popstate', (e) => {
  if (e.state?.collection) render(window.location.href, { push: false });
});
